/**
 * PostHog Analytics Service
 *
 * Provides analytics, error tracking, and session recording.
 * Session recording is disabled by default and enabled only after user authentication.
 */

import posthog from 'posthog-js';

const ENV = (import.meta as any).env;

let isInitialized = false;

/**
 * Initialize PostHog with session recording disabled by default.
 * Call this early in app initialization to capture pre-login errors.
 */
export const initPostHog = (appName?: string): void => {
  const apiKey = ENV.VITE_APP_POSTHOG_KEY;

  if (!apiKey) {
    console.warn('[PostHog] No API key found, analytics disabled');
    return;
  }

  if (isInitialized) {
    console.warn('[PostHog] Already initialized');
    return;
  } else {
    console.log('[PostHog] Initializing PostHog');
  }

  posthog.init(apiKey, {
    api_host: ENV.VITE_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
    capture_pageview: false, // We'll capture manually for SPA
    capture_pageleave: true,
    autocapture: false,
    disable_session_recording: true, // Disabled until user is authenticated
    persistence: 'localStorage',
    // Session recording configuration for better SPA support
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: false, // Allow email for support purposes
      },
      // Improve recording quality for SPAs
      recordCrossOriginIframes: false,
      inlineStylesheet: true,
      collectFonts: true,
      // Capture canvas elements if present
      recordCanvas: true,
      // Sampling for performance (1 = capture every mutation)
      sampling: {
        canvas: 2, // Capture canvas every 15fps
      },
    },
    // CM-5124 / FLEXO-6054: exception autocapture is enabled server-side (remote
    // config) and hooks window.onerror/onunhandledrejection regardless of
    // `autocapture`. Many captured rejections carry no usable type/message (e.g.
    // bare AxiosError rejections re-thrown by the http interceptor) and PostHog's
    // error-tracking pipeline can't fingerprint them, producing unprocessable
    // "$exception" events and the "exception processing" ingestion warning. Drop
    // those here so only real, fingerprintable exceptions reach PostHog.
    before_send: (event: any) => {
      if (event && event.event === '$exception') {
        const p: any = event.properties || {};
        const list = p.$exception_list;
        const hasUsableList =
          Array.isArray(list) && list.some((e: any) => e && (e.type || e.value));
        const hasMessage = !!p.$exception_message || !!p.$exception_type;
        if (!hasUsableList && !hasMessage) return null;
      }
      return event;
    },
  });

  // Register super property to distinguish events by app
  const source = appName || ENV.VITE_APP_NAME || 'unknown';
  posthog.register({ app_source: source });

  isInitialized = true;
};

/**
 * Check if PostHog is initialized and available
 */
export const isPostHogReady = (): boolean => {
  return isInitialized && !!ENV.VITE_APP_POSTHOG_KEY;
};

/**
 * Identify the user after successful authentication.
 * Also starts session recording for authenticated users.
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, any>
): void => {
  if (!isPostHogReady()) return;

  posthog.identify(userId, properties);
};

/**
 * Associate user with an organization group for aggregate analytics.
 */
export const setUserGroup = (
  groupType: string,
  groupId: string,
  properties?: Record<string, any>
): void => {
  if (!isPostHogReady()) return;

  posthog.group(groupType, groupId, properties);
};

/**
 * Start session recording for authenticated users.
 * Call this after successful login.
 */
export const startSessionRecording = (): void => {
  if (!isPostHogReady()) return;

  posthog.startSessionRecording();
};

/**
 * Stop session recording.
 * Call this on logout.
 */
export const stopSessionRecording = (): void => {
  if (!isPostHogReady()) return;

  posthog.stopSessionRecording();
};

/**
 * Reset PostHog identity on logout.
 * Stops session recording and clears user data.
 */
export const resetPostHog = (): void => {
  if (!isPostHogReady()) return;

  posthog.stopSessionRecording();
  posthog.reset();
};

/**
 * Capture an error event with context.
 * Use this in error handlers (e.g., HTTP interceptors).
 */
export const captureError = (
  error: Error | string,
  context?: Record<string, any>
): void => {
  if (!isPostHogReady()) return;

  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = typeof error === 'string' ? undefined : error.stack;

  posthog.capture('$exception', {
    $exception_message: errorMessage,
    $exception_stack_trace: errorStack,
    $exception_type: typeof error === 'string' ? 'string' : error.name,
    ...context,
  });
};

/**
 * Capture a custom event.
 */
export const captureEvent = (
  eventName: string,
  properties?: Record<string, any>
): void => {
  if (!isPostHogReady()) return;

  posthog.capture(eventName, properties);
};

/**
 * Capture a pageview manually.
 * Call this on SPA route changes for accurate page tracking.
 */
export const capturePageview = (path?: string): void => {
  if (!isPostHogReady()) return;

  posthog.capture('$pageview', {
    $current_url: path || window.location.href,
  });
};

/**
 * Capture page leave manually.
 * Call this before SPA navigation for accurate session recording.
 */
export const capturePageleave = (): void => {
  if (!isPostHogReady()) return;

  posthog.capture('$pageleave');
};

// Export the posthog instance for advanced usage
export { posthog };
